<template>
  <div class="dept-tree-panel">
    <div class="dept-tree-header">
      <span class="dept-tree-title">Phòng ban</span>
    </div>
    <div class="dept-tree-search">
      <b-form-input
        v-model="searchQuery"
        placeholder="Tìm phòng ban..."
        size="sm"
        @input="$emit('search', $event)"
      />
    </div>
    <div class="dept-tree-body">
      <div v-if="!filteredDepartments.length" class="dept-tree-empty">
        Không có phòng ban
      </div>
      <RecycleScroller
        v-else
        :items="flattenedItems"
        :item-size="itemSize"
        key-field="key"
        class="dept-tree-scroller"
      >
        <template #default="{ item }">
          <div
            v-if="item.type === 'dept'"
            class="dept-tree-node-header"
            :class="{ 'is-active': selectedDepartmentId === item.id }"
            @click="$emit('select', item.id)"
          >
            <span class="dept-tree-toggle" @click.stop="toggleNode(item.id)">
              <i :class="openNodes.includes(item.id) ? 'fas fa-caret-down' : 'fas fa-caret-right'" />
            </span>
            <span class="dept-tree-icon" :style="{ color: getDeptColor(item.id) }">
              <i :class="getDeptIcon(item.id)" />
            </span>
            <span class="dept-tree-label">{{ item.name }}</span>
            <span class="dept-tree-badge">{{ item.roleCount }}</span>
            <b-dropdown
              size="sm"
              variant="link"
              no-caret
              right
              class="dept-tree-actions"
              @click.native.stop
            >
              <template #button-content>
                <span class="dept-tree-action-btn">
                  <i class="fas fa-pencil-alt" />
                </span>
              </template>
              <b-dropdown-item @click="$emit('edit', item.dept)">
                <i class="fas fa-edit mr-2 text-primary" />Sửa phòng ban
              </b-dropdown-item>
              <template v-if="item.name !== 'SYSTEM'">
                <b-dropdown-divider />
                <b-dropdown-item @click="$emit('delete', item.dept)">
                  <i class="fas fa-trash-alt mr-2 text-danger" /><span class="text-danger">Xóa phòng ban</span>
                </b-dropdown-item>
              </template>
            </b-dropdown>
          </div>
          <div v-else class="dept-tree-child">
            <span class="dept-tree-child-icon">
              <inline-svg src="/media/svg/icons/General/User.svg" />
            </span>
            <span>{{ item.name }}</span>
          </div>
        </template>
      </RecycleScroller>
    </div>
  </div>
</template>

<script>
import { RecycleScroller } from 'vue-virtual-scroller';

export default {
  name: 'DeptTreePanel',
  components: { RecycleScroller },
  props: {
    departments: { type: Array, required: true },
    selectedDepartmentId: { type: String, default: null },
    initialSearch: { type: String, default: '' },
  },
  data() {
    return {
      searchQuery: this.initialSearch,
      openNodes: [],
      itemSize: 36,
    };
  },
  computed: {
    filteredDepartments() {
      if (!this.searchQuery) return this.departments;
      const q = this.searchQuery.toLowerCase();
      return this.departments.filter(d => d.name.toLowerCase().includes(q));
    },
    flattenedItems() {
      const items = [];
      this.filteredDepartments.forEach(dept => {
        items.push({
          key: `dept-${dept.id}`,
          type: 'dept',
          id: dept.id,
          name: dept.name,
          roleCount: dept.roles ? dept.roles.length : 0,
          dept,
        });
        if (this.openNodes.includes(dept.id)) {
          if (!dept.roles || !dept.roles.length) {
            items.push({
              key: `empty-${dept.id}`,
              type: 'role',
              name: 'Chưa có role',
              isEmpty: true,
            });
          } else {
            // Sort by role order: Staff, Support, Leader, Manager, Admin
            const roleOrder = { Staff: 1, Support: 2, Leader: 3, Manager: 4, Admin: 5 };
            const getShortName = (name) => {
              if (!name) return name;
              const idx = name.indexOf('_');
              return idx > 0 ? name.substring(0, idx) : name;
            };
            const sortedRoles = [...dept.roles].sort((a, b) => {
              const orderA = roleOrder[getShortName(a.roleName)] || 99;
              const orderB = roleOrder[getShortName(b.roleName)] || 99;
              return orderA - orderB;
            });
            sortedRoles.forEach(role => {
              const shortName = getShortName(role.roleName);
              items.push({
                key: `role-${role.id}`,
                type: 'role',
                name: shortName,
              });
            });
          }
        }
      });
      return items;
    },
  },
  methods: {
    toggleNode(id) {
      const idx = this.openNodes.indexOf(id);
      if (idx >= 0) {
        this.openNodes.splice(idx, 1);
      } else {
        this.openNodes.push(id);
      }
    },
    getDeptIcon(id) {
      const icons = [
        'fas fa-laptop-code', 'fas fa-heartbeat', 'fas fa-chart-bar', 'fas fa-users',
        'fas fa-shield-alt', 'fas fa-cogs', 'fas fa-microscope', 'fas fa-graduation-cap',
        'fas fa-briefcase', 'fas fa-stethoscope', 'fas fa-database', 'fas fa-paint-brush',
      ];
      const hash = id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
      return icons[hash % icons.length];
    },
    getDeptColor(id) {
      const colors = ['#3699ff', '#0bb783', '#f64e60', '#ffa800', '#8950fc', '#1bc5bd', '#e4b40c', '#ee6c4d'];
      const hash = id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
      return colors[hash % colors.length];
    },
  },
};
</script>

<style lang="scss" scoped>
.dept-tree-panel {
  background: #f8f9fc;
  border-right: 1px solid #e4e6ef;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.dept-tree-header {
  padding: 14px 16px 10px;
  border-bottom: 1px solid #e4e6ef;
}

.dept-tree-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7e8299;
}

.dept-tree-search {
  padding: 10px 12px;
  border-bottom: 1px solid #e4e6ef;
}

.dept-tree-body {
  flex: 1;
  overflow: hidden;
}

.dept-tree-scroller {
  height: 100%;
}

.dept-tree-empty {
  padding: 20px 16px;
  font-size: 13px;
  color: #b5b5c3;
  text-align: center;
}

.dept-tree-node-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  border-left: 3px solid transparent;
  transition: all 0.15s ease;
  gap: 6px;
  height: 36px;

  &:hover {
    background: #eef0f8;
  }

  &.is-active {
    background: #e8f5f3;
    border-left-color: #008479;

    .dept-tree-label {
      color: #008479;
      font-weight: 600;
    }
  }
}

.dept-tree-toggle {
  width: 14px;
  font-size: 11px;
  color: #a1a5b7;
  flex-shrink: 0;
}

.dept-tree-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.dept-tree-label {
  flex: 1;
  font-size: 13px;
  color: #3f4254;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dept-tree-badge {
  font-size: 10px;
  font-weight: 700;
  background: #e4e6ef;
  color: #7e8299;
  border-radius: 20px;
  padding: 1px 7px;
  flex-shrink: 0;

  .is-active & {
    background: #d0eeeb;
    color: #008479;
  }
}

.dept-tree-actions {
  display: none;
  flex-shrink: 0;
  margin-left: 4px;
}

.dept-tree-node-header:hover .dept-tree-actions {
  display: block;
}

.dept-tree-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #e4e6ef;
  color: #7e8299;
  font-size: 10px;
  cursor: pointer;

  &:hover {
    background: #3699ff;
    color: #fff;
  }
}

.dept-tree-child {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px 5px 36px;
  font-size: 12px;
  color: #5e6278;
  height: 36px;
}

.dept-tree-child-icon {
  width: 13px;
  height: 13px;
  color: #a1a5b7;

  ::v-deep svg {
    width: 13px;
    height: 13px;
  }
}
</style>
